import { useEffect, useState } from "react";
import useTranslation from "../../hooks/i18n/useTranslation";
import { IconSize } from "../../classes/enums/ButtonEnum";
import IconsEnum from "../../classes/enums/IconsEnum";
import { SPINESIM_MODALITIES } from "../../classes/enums/MedicalImagesEnum";
import IconButton from "../iconButton/IconButton";
import PainAssessmentThumbnail from "../painAssessmentThumbnail/PainAssessmentThumbnail";
import Thumbnail from "../thumbnail/Thumbnail";
import "./ThumbnailTab.css";

const ThumbnailTab = ({
  thumbnails,
  selectedThumbnail = null,
  onSelectThumbnail = () => {},
  visibleItems = 4,
}) => {
  const { t } = useTranslation();
  // State to track the starting index for visible thumbnails
  const [startIndex, setStartIndex] = useState(0);

  // Calculate the ending index for the visible range
  const endIndex = Math.min(startIndex + visibleItems, thumbnails?.length || 0);

  // Get only the thumbnails that should be visible in the current view
  const visibleThumbnails = thumbnails?.slice(startIndex, endIndex) || [];

  // Handle scrolling up (decreasing the start index)
  const handleScrollUp = () => {
    setStartIndex(prev => Math.max(0, prev - 1));
  };

  // Handle scrolling down (increasing the start index)
  const handleScrollDown = () => {
    setStartIndex(prev =>
      Math.min(prev + 1, (thumbnails?.length || 0) - visibleItems)
    );
  };

  useEffect(() => {
    if (thumbnails?.length === 0) return;
    setStartIndex(0);
  }, [thumbnails]);

  // Check if there are thumbnails to display
  if (!thumbnails || !Array.isArray(thumbnails) || thumbnails.length === 0) {
    return <div className="thumbnail-tab-empty"></div>;
  }

  // Determine if navigation buttons should be shown
  const showUpButton = startIndex > 0;
  const showDownButton = endIndex < (thumbnails?.length || 0);

  return (
    <div className="thumbnail-tab">
      {/* Always include the top navigation area, but conditionally render the button */}
      <div className="thumbnail-tab-navigation">
        {showUpButton ? (
          <IconButton
            icon={IconsEnum.ARROWUP}
            iconSize={IconSize.MEDIUM}
            noBgColor
            onClick={handleScrollUp}
            title={t("430")}
          />
        ) : (
          <div className="thumbnail-tab-navigation-placeholder" />
        )}
      </div>

      {/* Visible thumbnails */}
      <div className="thumbnail-tab-visible-items">
        {visibleThumbnails.map((thumbnail, index) => {
          // Calculate the actual index in the original array
          const actualIndex = startIndex + index;
          // Check if this item is selected
          const isSelected = thumbnail._id === selectedThumbnail;
          const thumbnailName =
            thumbnail.displayModality ||
            thumbnail.file_name ||
            thumbnail.title ||
            `Image ${actualIndex + 1}`;

          // Check if this is a pain assessment thumbnail
          if (thumbnail.modality === "Pain") {
            // The pain assessment data is in the instances array
            const painAssessmentData = thumbnail.instances?.[0];

            return (
              <div
                key={thumbnail._id}
                className={`thumbnail-tab-item ${isSelected ? "selected" : ""}`}
                onClick={() => onSelectThumbnail(actualIndex, thumbnail._id)}
              >
                <PainAssessmentThumbnail
                  assessment={painAssessmentData || thumbnail}
                  isSelected={isSelected}
                  onClick={() => onSelectThumbnail(actualIndex, thumbnail._id)}
                />
              </div>
            );
          }

          // Regular thumbnail logic for non-pain assessments
          let numberOfImages = 0;
          if (
            thumbnail.modality === SPINESIM_MODALITIES.CT ||
            thumbnail.modality === SPINESIM_MODALITIES.MR
          ) {
            numberOfImages = 3;
          } else {
            const instancesCount = thumbnail.instances?.length || 0;
            numberOfImages = instancesCount > 1 ? instancesCount : 0;
          }

          return (
            <div
              key={thumbnail._id}
              className={`thumbnail-tab-item ${isSelected ? "selected" : ""}`}
              onClick={() => onSelectThumbnail(actualIndex, thumbnail._id)}
            >
              <Thumbnail
                imageSrc={thumbnail.url}
                name={thumbnailName}
                date={thumbnail.date || thumbnail.creation_date}
                isSelected={isSelected}
                numberOfImages={numberOfImages}
              />
            </div>
          );
        })}
      </div>

      {/* Always include the bottom navigation area, but conditionally render the button */}
      <div className="thumbnail-tab-navigation">
        {showDownButton ? (
          <IconButton
            icon={IconsEnum.ARROWDOWN}
            iconSize={IconSize.MEDIUM}
            noBgColor
            onClick={handleScrollDown}
            title={t("431")}
          />
        ) : (
          <div className="thumbnail-tab-navigation-placeholder" />
        )}
      </div>
    </div>
  );
};

export default ThumbnailTab;
